/* Source: /home/sidsolaiyappan/skooch_ai/shared/priority_board.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { pbChipCategory } from "./chip_category.js";
const _jac = {
  int: {
    bit_length(n) {
      if (n === 0) return 0;
      return Math.floor(Math.log2(Math.abs(n))) + 1;
    },
    bit_count(n) {
      let x = Math.abs(n);
      let count = 0;
      while (x) { count += x & 1; x >>= 1; }
      return count;
    },
    to_bytes(n, length, byteorder) {
      const bytes = [];
      let val = n < 0 ? (1 << (length * 8)) + n : n;
      for (let i = 0; i < length; i++) {
        bytes.push(val & 0xff);
        val >>= 8;
      }
      if (byteorder === "big") bytes.reverse();
      return bytes;
    },
    from_bytes(bytes, byteorder) {
      const b = byteorder === "big" ? bytes : [...bytes].reverse();
      let result = 0;
      for (const byte of b) result = (result << 8) | byte;
      return result;
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  float: {
    as_integer_ratio(f) {
      if (!isFinite(f)) throw new _jac.exc.ValueError("cannot convert " + f + " to integer ratio");
      if (f === 0) return [0, 1];
      let num = f, den = 1;
      while (num !== Math.floor(num)) { num *= 2; den *= 2; }
      const g = _jac.float._gcd(Math.abs(num), den);
      return [num / g, den / g];
    },
    _gcd(a, b) { while (b) { [a, b] = [b, a % b]; } return a; },
    hex(f) {
      return f.toString(16);
    },
    fromhex(s) {
      return parseFloat(s);
    },
    mod(a, b) { return ((a % b) + b) % b; }
  },

  complex: {
    conjugate(c) { return {re: c.re, im: -c.im}; },
    add(a, b) { return {re: a.re + b.re, im: a.im + b.im}; },
    sub(a, b) { return {re: a.re - b.re, im: a.im - b.im}; },
    mul(a, b) { return {re: a.re * b.re - a.im * b.im, im: a.re * b.im + a.im * b.re}; },
    truediv(a, b) {
      const d = b.re * b.re + b.im * b.im;
      return {re: (a.re * b.re + a.im * b.im) / d, im: (a.im * b.re - a.re * b.im) / d};
    },
    pow(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      if (b.im === 0 && Number.isInteger(b.re)) {
        let result = {re: 1, im: 0};
        for (let i = 0; i < Math.abs(b.re); i++) result = _jac.complex.mul(result, a);
        return b.re < 0 ? _jac.complex.truediv({re: 1, im: 0}, result) : result;
      }
      const r = Math.sqrt(a.re * a.re + a.im * a.im);
      const theta = Math.atan2(a.im, a.re);
      const lnr = Math.log(r);
      const newR = Math.exp(lnr * b.re - theta * b.im);
      const newTheta = lnr * b.im + theta * b.re;
      return {re: newR * Math.cos(newTheta), im: newR * Math.sin(newTheta)};
    },
    eq(a, b) {
      if (typeof b === "number") b = {re: b, im: 0};
      if (typeof a === "number") a = {re: a, im: 0};
      return a.re === b.re && a.im === b.im;
    },
    neg(a) { return {re: -a.re, im: -a.im}; },
    pos(a) { return {re: +a.re, im: +a.im}; }
  },

  str: {
    capitalize(s) {
      if (s.length === 0) return s;
      return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
    },
    title(s) {
      return s.replace(/\b\w/g, c => c.toUpperCase());
    },
    swapcase(s) {
      return [...s].map(c => c === c.toUpperCase() ? c.toLowerCase() : c.toUpperCase()).join("");
    },
    count(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      if (sub === "") return t.length + 1;
      let count = 0, pos = 0;
      while ((pos = t.indexOf(sub, pos)) !== -1) { count++; pos += sub.length; }
      return count;
    },
    find(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.indexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    rfind(s, sub, start, end) {
      const t = s.slice(start || 0, end);
      const idx = t.lastIndexOf(sub);
      return idx === -1 ? -1 : idx + (start || 0);
    },
    index(s, sub, start, end) {
      const idx = _jac.str.find(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(s, sub, start, end) {
      const idx = _jac.str.rfind(s, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(s, prefix, start, end) {
      return s.slice(start || 0, end).startsWith(prefix);
    },
    endswith(s, suffix, start, end) {
      return s.slice(start || 0, end).endsWith(suffix);
    },
    replace(s, old, new_, count) {
      if (count === undefined) return s.split(old).join(new_);
      let result = s, n = 0;
      while (n < count) {
        const idx = result.indexOf(old);
        if (idx === -1) break;
        result = result.slice(0, idx) + new_ + result.slice(idx + old.length);
        n++;
      }
      return result;
    },
    strip(s, chars) {
      if (!chars) return s.trim();
      let start = 0, end = s.length;
      while (start < end && chars.indexOf(s[start]) !== -1) start++;
      while (end > start && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(start, end);
    },
    lstrip(s, chars) {
      if (!chars) return s.trimStart();
      let start = 0;
      while (start < s.length && chars.indexOf(s[start]) !== -1) start++;
      return s.slice(start);
    },
    rstrip(s, chars) {
      if (!chars) return s.trimEnd();
      let end = s.length;
      while (end > 0 && chars.indexOf(s[end - 1]) !== -1) end--;
      return s.slice(0, end);
    },
    removeprefix(s, prefix) {
      return s.startsWith(prefix) ? s.slice(prefix.length) : s;
    },
    removesuffix(s, suffix) {
      return (suffix && s.endsWith(suffix)) ? s.slice(0, -suffix.length) : s;
    },
    split(s, sep, maxsplit) {
      if (sep === undefined || sep === null) {
        const parts = s.trim().split(/\s+/);
        if (maxsplit === undefined) return parts;
        if (maxsplit <= 0) return [s];
        return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(" ")].filter(x => x !== undefined);
      }
      if (maxsplit === undefined) return s.split(sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      return [...parts.slice(0, maxsplit), parts.slice(maxsplit).join(sep)];
    },
    rsplit(s, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.str.split(s, sep);
      const parts = s.split(sep);
      if (parts.length <= maxsplit + 1) return parts;
      const keep = parts.length - maxsplit;
      return [parts.slice(0, keep).join(sep), ...parts.slice(keep)];
    },
    splitlines(s, keepends) {
      const lines = s.split(/(\r\n|\r|\n)/);
      const result = [];
      for (let i = 0; i < lines.length; i += 2) {
        const line = lines[i];
        const sep = lines[i + 1] || "";
        if (line || sep) result.push(keepends ? line + sep : line);
      }
      return result;
    },
    partition(s, sep) {
      const idx = s.indexOf(sep);
      if (idx === -1) return [s, "", ""];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    rpartition(s, sep) {
      const idx = s.lastIndexOf(sep);
      if (idx === -1) return ["", "", s];
      return [s.slice(0, idx), sep, s.slice(idx + sep.length)];
    },
    format(s, ...args) {
      let i = 0;
      return s.replace(/\{(\w*)\}/g, (match, key) => {
        if (key === "") return args[i++];
        if (!isNaN(key)) return args[parseInt(key)];
        return args[0] && typeof args[0] === "object" ? args[0][key] : match;
      });
    },
    format_map(s, mapping) {
      return s.replace(/\{(\w+)\}/g, (match, key) => key in mapping ? mapping[key] : match);
    },
    center(s, width, fillchar) {
      const fc = fillchar || " ";
      if (s.length >= width) return s;
      const total = width - s.length;
      const left = Math.floor(total / 2);
      return fc.repeat(left) + s + fc.repeat(total - left);
    },
    zfill(s, width) {
      if (s.length >= width) return s;
      const sign = (s[0] === "+" || s[0] === "-") ? s[0] : "";
      const body = sign ? s.slice(1) : s;
      return sign + "0".repeat(width - s.length) + body;
    },
    expandtabs(s, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      let result = "", col = 0;
      for (const c of s) {
        if (c === "\t") { const sp = ts - (col % ts); result += " ".repeat(sp); col += sp; }
        else if (c === "\n" || c === "\r") { result += c; col = 0; }
        else { result += c; col++; }
      }
      return result;
    },
    isnumeric(s) {
      if (s.length === 0) return false;
      return [...s].every(c => /[\d\u00B2\u00B3\u00B9\u00BC-\u00BE\u2150-\u218F]/.test(c));
    },
    istitle(s) {
      return s === _jac.str.title(s) && /[A-Za-z]/.test(s);
    },
    translate(s, table) {
      return [...s].map(c => {
        const code = c.charCodeAt(0);
        if (code in table) {
          const v = table[code];
          if (v === null) return "";
          if (typeof v === "number") return String.fromCharCode(v);
          return v;
        }
        return c;
      }).join("");
    },
    maketrans(...args) {
      const table = {};
      if (args.length === 1) {
        const mapping = args[0];
        for (const [k, v] of Object.entries(mapping)) {
          table[typeof k === "string" ? k.charCodeAt(0) : k] = v;
        }
      } else if (args.length >= 2) {
        const from_ = args[0], to_ = args[1];
        for (let i = 0; i < from_.length; i++) {
          table[from_.charCodeAt(i)] = to_.charCodeAt(i);
        }
        if (args[2]) {
          for (const c of args[2]) table[c.charCodeAt(0)] = null;
        }
      }
      return table;
    },
    mod(s, args) {
      let i = 0;
      const a = Array.isArray(args) ? args : [args];
      return s.replace(/%([sd%])/g, (m, fmt) => {
        if (fmt === "%") return "%";
        return String(a[i++]);
      });
    }
  },

  // Runtime-polymorphic method dispatch, used when the codegen cannot prove
  // the static type of a method-call receiver (e.g. a `dict`-typed prop, the
  // result of `or` widening, or a comprehension over an untyped value) and so
  // cannot pick a typed emitter. The compiler emits
  // `_jac.poly.call(obj, "name", ...args)`; `call` resolves the *runtime* type
  // of `obj` and applies the matching Python operation, forwards to a
  // same-named method on a user-defined object, or raises AttributeError. The
  // per-type tables below carry the trivial native-mapped methods inline and
  // delegate the non-trivial ones to the shared `_jac.str/list/dict/set`
  // helpers, so each operation has a single implementation and no Python
  // method name can leak verbatim into the emitted JavaScript.
  poly: {
    _typename(o) {
      if (o === null || o === undefined) return "NoneType";
      if (Array.isArray(o)) return "list";
      if (o instanceof Set) return "set";
      if (o instanceof Map) return "dict";
      if (o instanceof Uint8Array) return "bytes";
      const t = typeof o;
      if (t === "string") return "str";
      if (t === "boolean") return "bool";
      if (t === "number") return Number.isInteger(o) ? "int" : "float";
      if (t === "object") return (o.constructor && o.constructor.name) || "object";
      return t;
    },
    // --- per-runtime-type Python method tables (keyed by Python name) ---
    // Trivial native-mapped ops are inline; non-trivial ones delegate to the
    // shared `_jac.<type>` helpers (which the typed emitters also use), so the
    // typed and polymorphic paths share one implementation.
    _str: {
      lower: (s) => s.toLowerCase(),
      upper: (s) => s.toUpperCase(),
      casefold: (s) => s.toLowerCase(),
      strip: (s, chars) => {
        if (chars === undefined) return s.trim();
        let i = 0, j = s.length;
        while (i < j && chars.indexOf(s[i]) !== -1) i++;
        while (j > i && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(i, j);
      },
      lstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/^\s+/, "");
        let i = 0;
        while (i < s.length && chars.indexOf(s[i]) !== -1) i++;
        return s.slice(i);
      },
      rstrip: (s, chars) => {
        if (chars === undefined) return s.replace(/\s+$/, "");
        let j = s.length;
        while (j > 0 && chars.indexOf(s[j - 1]) !== -1) j--;
        return s.slice(0, j);
      }
    },
    _list: {
      append: (a, x) => { a.push(x); return null; },
      clear: (a) => { a.length = 0; return null; },
      copy: (a) => a.slice(),
      count: (a, x) => _jac.list.count(a, x),
      extend: (a, b) => { for (const v of b) a.push(v); return null; },
      index: (a, x, start, end) => _jac.list.index(a, x, start, end),
      insert: (a, i, x) => { a.splice(i, 0, x); return null; },
      pop: (a, i) => {
        if (i === undefined) return a.pop();
        const idx = i < 0 ? a.length + i : i;
        return a.splice(idx, 1)[0];
      },
      remove: (a, x) => _jac.list.remove(a, x),
      reverse: (a) => { a.reverse(); return null; },
      sort: (a, key) => _jac.list.sort(a, key)
    },
    _dict: {
      clear: (o) => _jac.dict.clear(o),
      copy: (o) => ({ ...o }),
      // Python `dict.get`: returns the stored value when the key is present
      // (even if it is null), and the default only when the key is absent.
      // A membership check is required — `o[key] ?? default_` would wrongly
      // substitute the default for a present-but-null value.
      get: (o, key, default_) =>
        Object.prototype.hasOwnProperty.call(o, key) ? o[key] : default_,
      items: (o) => Object.entries(o),
      keys: (o) => Object.keys(o),
      pop: (o, key, default_) => _jac.dict.pop(o, key, default_),
      popitem: (o) => _jac.dict.popitem(o),
      setdefault: (o, key, default_) => _jac.dict.setdefault(o, key, default_),
      update: (o, other) => {
        if (other instanceof Map) { for (const [k, v] of other) o[k] = v; }
        else if (Array.isArray(other)) { for (const [k, v] of other) o[k] = v; }
        else if (other) { for (const k of Object.keys(other)) o[k] = other[k]; }
        return null;
      },
      values: (o) => Object.values(o)
    },
    _set: {
      add: (s, x) => { s.add(x); return null; },
      clear: (s) => { s.clear(); return null; },
      copy: (s) => new Set(s),
      difference: (a, b) => _jac.set.difference(a, b),
      difference_update: (s, o) => _jac.set.difference_update(s, o),
      discard: (s, x) => { s.delete(x); return null; },
      intersection: (a, b) => _jac.set.intersection(a, b),
      intersection_update: (s, o) => _jac.set.intersection_update(s, o),
      isdisjoint: (a, b) => _jac.set.isDisjointFrom(a, b instanceof Set ? b : new Set(b)),
      issubset: (a, b) => _jac.set.isSubsetOf(a, b instanceof Set ? b : new Set(b)),
      issuperset: (a, b) => _jac.set.isSupersetOf(a, b instanceof Set ? b : new Set(b)),
      pop: (s) => _jac.set.pop(s),
      remove: (s, x) => _jac.set.remove(s, x),
      symmetric_difference: (a, b) => _jac.set.symmetricDifference(a, b),
      symmetric_difference_update: (s, o) => _jac.set.symmetric_difference_update(s, o),
      union: (a, b) => _jac.set.union(a, b),
      update: (s, o) => _jac.set.update(s, o)
    },
    // Jac dicts compile to plain objects, but interop code may hand us a real
    // Map; mirror the dict methods over the Map API for that case.
    _map: {
      clear: (m) => { m.clear(); return null; },
      copy: (m) => new Map(m),
      get: (m, key, default_) => (m.has(key) ? m.get(key) : default_),
      items: (m) => [...m.entries()],
      keys: (m) => [...m.keys()],
      pop: (m, key, default_) => {
        if (m.has(key)) { const v = m.get(key); m.delete(key); return v; }
        if (default_ !== undefined) return default_;
        throw new _jac.exc.KeyError("KeyError: " + key);
      },
      popitem: (m) => {
        if (m.size === 0) throw new _jac.exc.KeyError("dictionary is empty");
        const key = [...m.keys()].pop();
        const val = m.get(key);
        m.delete(key);
        return [key, val];
      },
      setdefault: (m, key, default_) => {
        if (!m.has(key)) m.set(key, default_ === undefined ? null : default_);
        return m.get(key);
      },
      update: (m, other) => {
        if (other instanceof Map) { for (const [k, v] of other) m.set(k, v); }
        else if (Array.isArray(other)) { for (const [k, v] of other) m.set(k, v); }
        else if (other) { for (const k of Object.keys(other)) m.set(k, other[k]); }
        return null;
      },
      values: (m) => [...m.values()]
    },
    // Single entry point. `obj.method(...args)` on a receiver whose static
    // type is not provably primitive is compiled to
    // `_jac.poly.call(obj, "method", ...args)`.
    call(o, m, ...args) {
      // 1. Genuine JS primitives get Python-semantic dispatch: a plain object
      //    is a Python dict, an Array is a list, a Set/Map/string/number/bytes
      //    maps to its Python counterpart. These are the types whose Python
      //    method semantics differ from (or have no) native JS equivalent.
      if (typeof o === "string") {
        if (_jac.poly._str[m]) return _jac.poly._str[m](o, ...args);
        if (_jac.str[m]) return _jac.str[m](o, ...args);
      } else if (typeof o === "number") {
        const ns = Number.isInteger(o) ? _jac.int : _jac.float;
        if (ns && ns[m]) return ns[m](o, ...args);
      } else if (Array.isArray(o)) {
        if (_jac.poly._list[m]) return _jac.poly._list[m](o, ...args);
      } else if (o instanceof Set) {
        if (_jac.poly._set[m]) return _jac.poly._set[m](o, ...args);
      } else if (o instanceof Map) {
        if (_jac.poly._map[m]) return _jac.poly._map[m](o, ...args);
      } else if (o instanceof Uint8Array) {
        if (_jac.bytes[m]) return _jac.bytes[m](o, ...args);
      } else if (
        o !== null && o !== undefined && typeof o === "object"
        && (Object.getPrototypeOf(o) === Object.prototype
            || Object.getPrototypeOf(o) === null)
      ) {
        // A plain object is a Python dict literal.
        if (_jac.poly._dict[m]) return _jac.poly._dict[m](o, ...args);
      }
      // 2. Otherwise forward to a same-named method the receiver carries
      //    itself: a user-defined class instance, a host/framework object
      //    (Headers, URLSearchParams, ...), a callable with attached methods,
      //    or a Set/Map/Array method not mirrored above. This matches the
      //    direct `obj.method(...)` that a typed call would have emitted.
      if (o !== null && o !== undefined && typeof o[m] === "function") {
        return o[m](...args);
      }
      // 3. Nothing applies: raise the Python-style error instead of leaking a
      //    method name or silently returning a wrong value.
      throw new _jac.exc.AttributeError(
        "'" + _jac.poly._typename(o) + "' object has no attribute '" + m + "'");
    },
    // Backward-compatible named shims. The compiler now emits `call`, but a
    // previously-built bundle may still reference a named helper; these route
    // it through the same dispatcher (rest params preserve the arg count).
    lower(s, ...r) { return _jac.poly.call(s, "lower", ...r); },
    upper(s, ...r) { return _jac.poly.call(s, "upper", ...r); },
    strip(s, ...r) { return _jac.poly.call(s, "strip", ...r); },
    lstrip(s, ...r) { return _jac.poly.call(s, "lstrip", ...r); },
    rstrip(s, ...r) { return _jac.poly.call(s, "rstrip", ...r); },
    append(a, ...r) { return _jac.poly.call(a, "append", ...r); },
    extend(a, ...r) { return _jac.poly.call(a, "extend", ...r); },
    pop(a, ...r) { return _jac.poly.call(a, "pop", ...r); },
    get(o, ...r) { return _jac.poly.call(o, "get", ...r); },
    contains(c, x) {
      // Python-equivalent membership for a value of unknown static type.
      // string -> substring; array (list/tuple/range) -> element;
      // Set/Map -> key/element; plain object (dict) -> own key.
      if (typeof c === "string") return c.includes(x);
      if (Array.isArray(c)) return c.includes(x);
      if (c instanceof Set || c instanceof Map) return c.has(x);
      if (c !== null && typeof c === "object") {
        return Object.prototype.hasOwnProperty.call(c, x);
      }
      return false;
    },
    getitem(c, i) {
      // Subscript read for a value of unknown static type. This is a faithful
      // superset of a raw `c[i]`: it only adds Python's negative indexing for
      // sequences (string/array) and otherwise preserves JS semantics exactly
      // (mapping/object access, out-of-range -> `undefined`, no new throws).
      if (typeof i === "number" && i < 0
          && (typeof c === "string" || Array.isArray(c))) {
        return c[c.length + i];
      }
      if (c instanceof Map) return c.get(i);
      return c[i];
    },
    setitem(c, i, v) {
      // Subscript assignment for a value of unknown/list static type. A
      // negative index writes the array element counted from the end (a raw
      // JS `c[-1] = v` would instead create a bogus "-1" property); every
      // other case behaves like a plain `c[i] = v`.
      if (typeof i === "number" && i < 0 && Array.isArray(c)) {
        c[c.length + i] = v;
        return v;
      }
      if (c instanceof Map) { c.set(i, v); return v; }
      c[i] = v;
      return v;
    },
    and_(a, bf) {
      // Python `a and b`: returns `a` when it is falsy, else evaluates and
      // returns `b`. `bf` is a thunk so the right operand keeps short-circuit
      // (lazy) evaluation, and `a` is evaluated exactly once by the caller.
      return _jac.builtin.bool(a) ? bf() : a;
    },
    or_(a, bf) {
      // Python `a or b`: returns `a` when it is truthy, else `b`.
      return _jac.builtin.bool(a) ? a : bf();
    }
  },

  list: {
    remove(arr, val) {
      const i = arr.indexOf(val);
      if (i === -1) throw new _jac.exc.ValueError("list.remove(x): x not in list");
      arr.splice(i, 1);
    },
    index(arr, val, start, end) {
      const s = start || 0;
      const e = end === undefined ? arr.length : end;
      for (let i = s; i < e; i++) {
        if (arr[i] === val) return i;
      }
      throw new _jac.exc.ValueError(val + " is not in list");
    },
    count(arr, val) {
      let c = 0;
      for (const item of arr) { if (item === val) c++; }
      return c;
    },
    sort(arr, key) {
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
    },
    repeat(arr, n) {
      const result = [];
      for (let i = 0; i < n; i++) result.push(...arr);
      return result;
    },
    eq(a, b) { return JSON.stringify(a) === JSON.stringify(b); },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.list.lt(b, a); },
    le(a, b) { return _jac.list.eq(a, b) || _jac.list.lt(a, b); },
    ge(a, b) { return _jac.list.eq(a, b) || _jac.list.gt(a, b); },
    imul(arr, n) {
      const orig = [...arr];
      arr.length = 0;
      for (let i = 0; i < n; i++) arr.push(...orig);
      return arr;
    }
  },

  dict: {
    pop(d, key, default_) {
      if (key in d) { const v = d[key]; delete d[key]; return v; }
      if (default_ !== undefined) return default_;
      throw new _jac.exc.KeyError("KeyError: " + key);
    },
    popitem(d) {
      const keys = Object.keys(d);
      if (keys.length === 0) throw new _jac.exc.KeyError("dictionary is empty");
      const key = keys[keys.length - 1];
      const val = d[key];
      delete d[key];
      return [key, val];
    },
    setdefault(d, key, default_) {
      if (!(key in d)) d[key] = default_ === undefined ? null : default_;
      return d[key];
    },
    clear(d) {
      for (const key of Object.keys(d)) delete d[key];
    },
    fromkeys(keys, value) {
      const d = {};
      const v = value === undefined ? null : value;
      for (const k of keys) d[k] = v;
      return d;
    },
    eq(a, b) {
      const ka = Object.keys(a), kb = Object.keys(b);
      if (ka.length !== kb.length) return false;
      for (const k of ka) {
        if (!(k in b)) return false;
        const va = a[k], vb = b[k];
        if (va !== vb) {
          if (typeof va === "object" && typeof vb === "object" && va !== null && vb !== null) {
            if (JSON.stringify(va) !== JSON.stringify(vb)) return false;
          } else return false;
        }
      }
      return true;
    }
  },

  set: {
    remove(s, val) {
      if (!s.has(val)) throw new _jac.exc.KeyError("KeyError: " + val);
      s.delete(val);
    },
    pop(s) {
      if (s.size === 0) throw new _jac.exc.KeyError("pop from an empty set");
      const val = s.values().next().value;
      s.delete(val);
      return val;
    },
    update(s, other) { for (const v of other) s.add(v); },
    intersection_update(s, other) {
      const otherSet = other instanceof Set ? other : new Set(other);
      for (const v of s) { if (!otherSet.has(v)) s.delete(v); }
    },
    difference_update(s, other) {
      for (const v of other) s.delete(v);
    },
    symmetric_difference_update(s, other) {
      for (const v of other) { if (s.has(v)) s.delete(v); else s.add(v); }
    },
    union(a, b) {
      const r = new Set(a);
      for (const v of b) r.add(v);
      return r;
    },
    intersection(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (bs.has(v)) r.add(v); }
      return r;
    },
    difference(a, b) {
      const bs = b instanceof Set ? b : new Set(b);
      const r = new Set();
      for (const v of a) { if (!bs.has(v)) r.add(v); }
      return r;
    },
    symmetricDifference(a, b) {
      const r = new Set(a);
      for (const v of b) { if (r.has(v)) r.delete(v); else r.add(v); }
      return r;
    },
    isSubsetOf(a, b) {
      if (a.size > b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    isSupersetOf(a, b) { return _jac.set.isSubsetOf(b, a); },
    isDisjointFrom(a, b) {
      for (const v of a) { if (b.has(v)) return false; }
      return true;
    },
    is_proper_subset(a, b) {
      if (a.size >= b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    },
    is_proper_superset(a, b) { return _jac.set.is_proper_subset(b, a); },
    eq(a, b) {
      if (a.size !== b.size) return false;
      for (const v of a) { if (!b.has(v)) return false; }
      return true;
    }
  },

  bytes: {
    hex(b) {
      return Array.from(b).map(x => x.toString(16).padStart(2, "0")).join("");
    },
    fromhex(s) {
      const arr = [];
      const clean = s.replace(/\s/g, "");
      for (let i = 0; i < clean.length; i += 2) arr.push(parseInt(clean.substr(i, 2), 16));
      return new Uint8Array(arr);
    },
    _indexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return s;
      for (let i = s; i <= e - sl; i++) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    _lastIndexOf(b, sub, start, end) {
      const s = start || 0;
      const e = end === undefined ? b.length : end;
      const sl = sub.length;
      if (sl === 0) return e;
      for (let i = e - sl; i >= s; i--) {
        let m = true;
        for (let j = 0; j < sl; j++) { if (b[i + j] !== sub[j]) { m = false; break; } }
        if (m) return i;
      }
      return -1;
    },
    count(b, sub) {
      let c = 0, p = 0;
      while (p <= b.length - sub.length) {
        const idx = _jac.bytes._indexOf(b, sub, p);
        if (idx === -1) break;
        c++; p = idx + sub.length;
      }
      return c;
    },
    find(b, sub, start, end) { return _jac.bytes._indexOf(b, sub, start, end); },
    rfind(b, sub, start, end) { return _jac.bytes._lastIndexOf(b, sub, start, end); },
    index(b, sub, start, end) {
      const idx = _jac.bytes._indexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    rindex(b, sub, start, end) {
      const idx = _jac.bytes._lastIndexOf(b, sub, start, end);
      if (idx === -1) throw new _jac.exc.ValueError("substring not found");
      return idx;
    },
    startswith(b, prefix, start, end) {
      const s = start || 0;
      if (prefix.length > b.length - s) return false;
      for (let i = 0; i < prefix.length; i++) { if (b[s + i] !== prefix[i]) return false; }
      return true;
    },
    endswith(b, suffix, start, end) {
      const e = end === undefined ? b.length : end;
      if (suffix.length > e) return false;
      const off = e - suffix.length;
      for (let i = 0; i < suffix.length; i++) { if (b[off + i] !== suffix[i]) return false; }
      return true;
    },
    replace(b, old_, new_, count) {
      const r = [];
      let pos = 0, n = 0;
      const max = count === undefined ? Infinity : count;
      while (pos <= b.length) {
        if (n < max) {
          const idx = _jac.bytes._indexOf(b, old_, pos);
          if (idx !== -1) {
            for (let i = pos; i < idx; i++) r.push(b[i]);
            for (let i = 0; i < new_.length; i++) r.push(new_[i]);
            pos = idx + old_.length; n++;
            continue;
          }
        }
        for (let i = pos; i < b.length; i++) r.push(b[i]);
        break;
      }
      return new Uint8Array(r);
    },
    _isWS(c) { return c === 32 || c === 9 || c === 10 || c === 13 || c === 11 || c === 12; },
    strip(b, chars) {
      let s = 0, e = b.length;
      if (chars) { const cs = new Set(chars); while (s < e && cs.has(b[s])) s++; while (e > s && cs.has(b[e-1])) e--; }
      else { while (s < e && _jac.bytes._isWS(b[s])) s++; while (e > s && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(s, e);
    },
    lstrip(b, chars) {
      let s = 0;
      if (chars) { const cs = new Set(chars); while (s < b.length && cs.has(b[s])) s++; }
      else { while (s < b.length && _jac.bytes._isWS(b[s])) s++; }
      return b.slice(s);
    },
    rstrip(b, chars) {
      let e = b.length;
      if (chars) { const cs = new Set(chars); while (e > 0 && cs.has(b[e-1])) e--; }
      else { while (e > 0 && _jac.bytes._isWS(b[e-1])) e--; }
      return b.slice(0, e);
    },
    removeprefix(b, prefix) {
      if (_jac.bytes.startswith(b, prefix)) return b.slice(prefix.length);
      return b.slice();
    },
    removesuffix(b, suffix) {
      if (suffix.length > 0 && _jac.bytes.endswith(b, suffix)) return b.slice(0, b.length - suffix.length);
      return b.slice();
    },
    split(b, sep, maxsplit) {
      const parts = [];
      let pos = 0, n = 0;
      const max = maxsplit === undefined ? Infinity : maxsplit;
      while (pos <= b.length) {
        if (n >= max) { parts.push(b.slice(pos)); return parts; }
        const idx = _jac.bytes._indexOf(b, sep, pos);
        if (idx === -1) { parts.push(b.slice(pos)); return parts; }
        parts.push(b.slice(pos, idx));
        pos = idx + sep.length; n++;
      }
      if (pos <= b.length) parts.push(b.slice(pos));
      return parts;
    },
    rsplit(b, sep, maxsplit) {
      if (maxsplit === undefined) return _jac.bytes.split(b, sep);
      const parts = [];
      let end = b.length, n = 0;
      while (end >= 0 && n < maxsplit) {
        const idx = _jac.bytes._lastIndexOf(b, sep, 0, end);
        if (idx === -1) break;
        parts.unshift(b.slice(idx + sep.length, end));
        end = idx; n++;
      }
      parts.unshift(b.slice(0, end));
      return parts;
    },
    splitlines(b, keepends) {
      const result = [];
      let start = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 10 || b[i] === 13) {
          let eol = i + 1;
          if (b[i] === 13 && i + 1 < b.length && b[i + 1] === 10) eol++;
          result.push(keepends ? b.slice(start, eol) : b.slice(start, i));
          start = eol;
          if (eol > i + 1) i++;
        }
      }
      if (start < b.length) result.push(b.slice(start));
      return result;
    },
    join(sep, parts) {
      if (parts.length === 0) return new Uint8Array(0);
      const r = [];
      for (let i = 0; i < parts.length; i++) {
        if (i > 0) for (const x of sep) r.push(x);
        for (const x of parts[i]) r.push(x);
      }
      return new Uint8Array(r);
    },
    partition(b, sep) {
      const idx = _jac.bytes._indexOf(b, sep);
      if (idx === -1) return [b.slice(), new Uint8Array(0), new Uint8Array(0)];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    rpartition(b, sep) {
      const idx = _jac.bytes._lastIndexOf(b, sep);
      if (idx === -1) return [new Uint8Array(0), new Uint8Array(0), b.slice()];
      return [b.slice(0, idx), sep.slice(), b.slice(idx + sep.length)];
    },
    _isUp(c) { return c >= 65 && c <= 90; },
    _isLo(c) { return c >= 97 && c <= 122; },
    _isAl(c) { return (c >= 65 && c <= 90) || (c >= 97 && c <= 122); },
    _isDig(c) { return c >= 48 && c <= 57; },
    capitalize(b) {
      if (b.length === 0) return new Uint8Array(0);
      const r = new Uint8Array(b.length);
      r[0] = _jac.bytes._isLo(b[0]) ? b[0] - 32 : b[0];
      for (let i = 1; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    lower(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i];
      return r;
    },
    upper(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) r[i] = _jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i];
      return r;
    },
    title(b) {
      const r = new Uint8Array(b.length);
      let prev = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isAl(b[i])) {
          r[i] = prev ? (_jac.bytes._isUp(b[i]) ? b[i] + 32 : b[i]) : (_jac.bytes._isLo(b[i]) ? b[i] - 32 : b[i]);
          prev = true;
        } else { r[i] = b[i]; prev = false; }
      }
      return r;
    },
    swapcase(b) {
      const r = new Uint8Array(b.length);
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) r[i] = b[i] + 32;
        else if (_jac.bytes._isLo(b[i])) r[i] = b[i] - 32;
        else r[i] = b[i];
      }
      return r;
    },
    center(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const total = width - b.length;
      const left = Math.floor(total / 2);
      const r = new Uint8Array(width);
      r.fill(f, 0, left); r.set(b, left); r.fill(f, left + b.length);
      return r;
    },
    ljust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      r.set(b); r.fill(f, b.length);
      return r;
    },
    rjust(b, width, fb) {
      const f = fb || 32;
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      const off = width - b.length;
      r.fill(f, 0, off); r.set(b, off);
      return r;
    },
    zfill(b, width) {
      if (b.length >= width) return b.slice();
      const r = new Uint8Array(width);
      let off = 0;
      if (b.length > 0 && (b[0] === 43 || b[0] === 45)) { r[0] = b[0]; off = 1; }
      const pad = width - b.length;
      r.fill(48, off, off + pad);
      for (let i = off; i < b.length; i++) r[pad + i] = b[i];
      return r;
    },
    expandtabs(b, tabsize) {
      const ts = tabsize === undefined ? 8 : tabsize;
      const r = [];
      let col = 0;
      for (let i = 0; i < b.length; i++) {
        if (b[i] === 9) { const sp = ts - (col % ts); for (let j = 0; j < sp; j++) r.push(32); col += sp; }
        else if (b[i] === 10 || b[i] === 13) { r.push(b[i]); col = 0; }
        else { r.push(b[i]); col++; }
      }
      return new Uint8Array(r);
    },
    translate(b, table, del_) {
      const r = [];
      const ds = del_ ? new Set(del_) : null;
      for (let i = 0; i < b.length; i++) {
        if (ds && ds.has(b[i])) continue;
        r.push(table && table[b[i]] !== undefined ? table[b[i]] : b[i]);
      }
      return new Uint8Array(r);
    },
    maketrans(from_, to_) {
      const t = new Uint8Array(256);
      for (let i = 0; i < 256; i++) t[i] = i;
      for (let i = 0; i < from_.length; i++) t[from_[i]] = to_[i];
      return t;
    },
    isalnum(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i]) && !_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    isalpha(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isAl(b[i])) return false;
      return true;
    },
    isascii(b) {
      for (let i = 0; i < b.length; i++) if (b[i] > 127) return false;
      return true;
    },
    isdigit(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isDig(b[i])) return false;
      return true;
    },
    islower(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) return false;
        if (_jac.bytes._isLo(b[i])) has = true;
      }
      return has;
    },
    isupper(b) {
      let has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isLo(b[i])) return false;
        if (_jac.bytes._isUp(b[i])) has = true;
      }
      return has;
    },
    isspace(b) {
      if (b.length === 0) return false;
      for (let i = 0; i < b.length; i++) if (!_jac.bytes._isWS(b[i])) return false;
      return true;
    },
    istitle(b) {
      let prev = false, has = false;
      for (let i = 0; i < b.length; i++) {
        if (_jac.bytes._isUp(b[i])) { if (prev) return false; prev = true; has = true; }
        else if (_jac.bytes._isLo(b[i])) { if (!prev) return false; prev = true; has = true; }
        else prev = false;
      }
      return has;
    },
    add(a, b) {
      const r = new Uint8Array(a.length + b.length);
      r.set(a); r.set(b, a.length);
      return r;
    },
    mul(b, n) {
      const r = new Uint8Array(b.length * n);
      for (let i = 0; i < n; i++) r.set(b, i * b.length);
      return r;
    },
    mod(b, args) {
      const s = new TextDecoder().decode(b);
      return new TextEncoder().encode(_jac.str.mod(s, args));
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
      return true;
    },
    lt(a, b) {
      for (let i = 0; i < Math.min(a.length, b.length); i++) {
        if (a[i] < b[i]) return true;
        if (a[i] > b[i]) return false;
      }
      return a.length < b.length;
    },
    gt(a, b) { return _jac.bytes.lt(b, a); },
    le(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.lt(a, b); },
    ge(a, b) { return _jac.bytes.eq(a, b) || _jac.bytes.gt(a, b); },
    contains(b, sub) { return _jac.bytes._indexOf(b, sub) !== -1; }
  },

  tuple: {
    count(arr, val) { return _jac.list.count(arr, val); },
    index(arr, val, start, end) { return _jac.list.index(arr, val, start, end); },
    repeat(arr, n) { return _jac.list.repeat(arr, n); },
    eq(a, b) { return _jac.list.eq(a, b); },
    lt(a, b) { return _jac.list.lt(a, b); },
    gt(a, b) { return _jac.list.gt(a, b); },
    le(a, b) { return _jac.list.le(a, b); },
    ge(a, b) { return _jac.list.ge(a, b); }
  },

  range: {
    count(r, val) {
      if (typeof r[Symbol.iterator] === "function") {
        let c = 0;
        for (const v of r) { if (v === val) c++; }
        return c;
      }
      return 0;
    },
    index(r, val) {
      let i = 0;
      for (const v of r) { if (v === val) return i; i++; }
      throw new _jac.exc.ValueError(val + " is not in range");
    },
    eq(a, b) {
      if (a.length !== b.length) return false;
      for (let i = 0; i < a.length; i++) { if (a[i] !== b[i]) return false; }
      return true;
    },
    contains(r, val) {
      for (const v of r) { if (v === val) return true; }
      return false;
    }
  },

  builtin: {
    round(n, ndigits) {
      if (ndigits === undefined) return Math.round(n);
      const f = Math.pow(10, ndigits);
      return Math.round(n * f) / f;
    },
    sum(iterable, start) {
      let total = start || 0;
      for (const v of iterable) total += v;
      return total;
    },
    sorted(iterable, key, reverse) {
      const arr = [...iterable];
      if (key) {
        arr.sort((a, b) => {
          const ka = key(a), kb = key(b);
          return ka < kb ? -1 : ka > kb ? 1 : 0;
        });
      } else {
        arr.sort((a, b) => a < b ? -1 : a > b ? 1 : 0);
      }
      if (reverse) arr.reverse();
      return arr;
    },
    enumerate(iterable, start) {
      const s = start || 0;
      return [...iterable].map((v, i) => [s + i, v]);
    },
    zip(...iterables) {
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(arrays.map(a => a[i]));
      }
      return result;
    },
    map(fn, ...iterables) {
      if (iterables.length === 1) return [...iterables[0]].map(fn);
      const arrays = iterables.map(it => [...it]);
      const len = Math.min(...arrays.map(a => a.length));
      const result = [];
      for (let i = 0; i < len; i++) {
        result.push(fn(...arrays.map(a => a[i])));
      }
      return result;
    },
    filter(fn, iterable) {
      return [...iterable].filter(fn || (x => x));
    },
    // Python subclass relation over builtin-type *tags* -- the string names
    // (`"str"`, `"int"`, ...) that `exit_builtin_type` lowers builtin types to.
    // `object` is the universal base and `bool` is a subclass of `int`; `tuple`
    // and `frozenset` share JS representations with `list`/`set`, so fold those
    // aliases to their runtime tag first. One source of truth so isinstance and
    // issubclass can never disagree.
    _tagSubclass(subTag, superTag) {
      const fold = (t) => t === "tuple" ? "list" : t === "frozenset" ? "set" : t;
      const sub = fold(subTag), sup = fold(superTag);
      if (sup === "object") return true;
      if (sub === sup) return true;
      if (sup === "int" && sub === "bool") return true;
      return false;
    },
    isinstance(o, classinfo) {
      // isinstance(x, (A, B, ...)) -- the tuple of types lowers to a JS array;
      // match if `o` is an instance of any member.
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.isinstance(o, c)) return true;
        }
        return false;
      }
      // Builtin types lower to a string tag (their Python type name): compare
      // the value's runtime type against it via the shared subclass relation.
      if (typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(_jac.poly._typename(o), classinfo);
      }
      // User archetype / JS constructor.
      if (typeof classinfo === "function") return o instanceof classinfo;
      return false;
    },
    issubclass(cls, classinfo) {
      if (Array.isArray(classinfo)) {
        for (const c of classinfo) {
          if (_jac.builtin.issubclass(cls, c)) return true;
        }
        return false;
      }
      // When either side is a builtin-type tag, compare by the shared subclass
      // relation (covers `object`, `bool` ⊂ `int`, and identity).
      if (typeof cls === "string" || typeof classinfo === "string") {
        return _jac.builtin._tagSubclass(cls, classinfo);
      }
      if (cls === classinfo) return true;
      let proto = cls;
      while (proto) {
        if (proto === classinfo) return true;
        proto = Object.getPrototypeOf(proto);
      }
      return false;
    },
    id(o) {
      if (o === null || o === undefined) return 0;
      if (typeof o === "number") return o;
      return typeof o === "string" ? _jac.builtin._strHash(o) : 0;
    },
    _strHash(s) {
      let h = 0;
      for (let i = 0; i < s.length; i++) {
        h = ((h << 5) - h + s.charCodeAt(i)) | 0;
      }
      return h;
    },
    jid(obj) {
      if (obj === null || obj === undefined) {
        throw new _jac.exc.TypeError(
          "jid() called on " + String(obj)
          + " -- the server may have returned an error instead of a node."
          + " Check that your endpoint returned successfully before using jid()."
        );
      }
      if (obj._jac_id === undefined) {
        throw new _jac.exc.TypeError(
          "jid() expected a node or edge with graph identity, but received "
          + (typeof obj === "object" ? JSON.stringify(Object.keys(obj)) : typeof obj)
          + ". Only node and edge archetypes have a jid."
        );
      }
      return obj._jac_id;
    },
    // unsafe_html(x) returns a sentinel object that the JSX flattener
    // recognises and renders as raw HTML via dangerouslySetInnerHTML.
    // Named "unsafe" so the security implication is visible at the use site.
    unsafe_html(html) {
      return { __jacUnsafeHtml: String(html == null ? "" : html) };
    },
    hash(obj) { return _jac.builtin.id(obj); },
    repr(obj) { return JSON.stringify(obj); },
    pow(base, exp, mod) {
      let result = Math.pow(base, exp);
      if (mod !== undefined) result = ((result % mod) + mod) % mod;
      return result;
    },
    next(iter, default_) {
      const result = iter.next();
      if (result.done) {
        if (default_ !== undefined) return default_;
        throw new _jac.exc.StopIteration("StopIteration");
      }
      return result.value;
    },
    vars(o) {
      if (o === undefined) return {};
      const result = {};
      for (const [k, v] of Object.entries(o)) {
        if (typeof v !== "function") result[k] = v;
      }
      return result;
    },
    dir(o) {
      if (o === undefined) return [];
      return Object.getOwnPropertyNames(o).sort();
    },
    open() { throw new _jac.exc.OSError("open() is not supported in client-side JavaScript"); },
    format(value, format_spec) {
      if (!format_spec) return String(value);
      return String(value);
    },
    ascii(obj) {
      if (typeof obj === "string") {
        let r = "'";
        for (const c of obj) {
          const code = c.charCodeAt(0);
          if (c === "\\") r += "\\\\";
          else if (c === "'") r += "\\'";
          else if (code >= 0x20 && code <= 0x7e) r += c;
          else if (code < 0x100) r += "\\x" + code.toString(16).padStart(2, "0");
          else r += "\\u" + code.toString(16).padStart(4, "0");
        }
        return r + "'";
      }
      return String(obj);
    },
    complex(re, im) { return {re: re || 0, im: im || 0}; },
    bool(x) {
      if (x === null || x === undefined || x === false || x === 0 || x === "" || Number.isNaN(x)) return false;
      if (Array.isArray(x)) return x.length > 0;
      if (x instanceof Set || x instanceof Map) return x.size > 0;
      if (typeof x === "object") return Object.keys(x).length > 0;
      return true;
    },
    range(...args) {
      let start, stop, step;
      if (args.length === 1) { start = 0; stop = args[0]; step = 1; }
      else if (args.length === 2) { start = args[0]; stop = args[1]; step = 1; }
      else { start = args[0]; stop = args[1]; step = args[2]; }
      if (step === 0) throw new _jac.exc.ValueError("range() arg 3 must not be zero");
      const result = [];
      if (step > 0) { for (let i = start; i < stop; i += step) result.push(i); }
      else { for (let i = start; i > stop; i += step) result.push(i); }
      return result;
    },
    slice(...args) {
      if (args.length === 1) return { start: null, stop: args[0], step: null };
      return { start: args[0] !== undefined ? args[0] : null, stop: args[1] !== undefined ? args[1] : null, step: args[2] !== undefined ? args[2] : null };
    },
    format(value, format_spec) {
      if (!format_spec || format_spec === "") return String(value);
      const m = format_spec.match(/^([<>^]?)(\d+)?(?:\.(\d+))?([dfseboxXn%]?)$/);
      if (!m) return String(value);
      const [, align, widthStr, precStr, ftype] = m;
      let s;
      const prec = precStr !== undefined ? parseInt(precStr) : undefined;
      if (ftype === "f" || ftype === "") {
        s = prec !== undefined ? Number(value).toFixed(prec) : String(value);
      } else if (ftype === "d") {
        s = String(Math.floor(Number(value)));
      } else if (ftype === "b") {
        s = Number(value).toString(2);
      } else if (ftype === "o") {
        s = Number(value).toString(8);
      } else if (ftype === "x") {
        s = Number(value).toString(16);
      } else if (ftype === "X") {
        s = Number(value).toString(16).toUpperCase();
      } else if (ftype === "e") {
        s = prec !== undefined ? Number(value).toExponential(prec) : Number(value).toExponential();
      } else if (ftype === "%") {
        s = (prec !== undefined ? (Number(value) * 100).toFixed(prec) : (Number(value) * 100).toFixed(6)) + "%";
      } else if (ftype === "s" || ftype === "n") {
        s = String(value);
      } else {
        s = String(value);
      }
      if (widthStr) {
        const width = parseInt(widthStr);
        if (s.length < width) {
          const pad = " ".repeat(width - s.length);
          if (align === "<") s = s + pad;
          else if (align === "^") {
            const left = Math.floor((width - s.length) / 2);
            s = " ".repeat(left) + s + " ".repeat(width - s.length - left);
          }
          else s = pad + s;
        }
      }
      return s;
    }
  },

  exc: (() => {
    class BaseException extends Error { constructor(m) { super(m); this.name = 'BaseException'; } }
    class Exception extends BaseException { constructor(m) { super(m); this.name = 'Exception'; } }
    class ArithmeticError extends Exception { constructor(m) { super(m); this.name = 'ArithmeticError'; } }
    class ZeroDivisionError extends ArithmeticError { constructor(m) { super(m); this.name = 'ZeroDivisionError'; } }
    class OverflowError extends ArithmeticError { constructor(m) { super(m); this.name = 'OverflowError'; } }
    class FloatingPointError extends ArithmeticError { constructor(m) { super(m); this.name = 'FloatingPointError'; } }
    class LookupError extends Exception { constructor(m) { super(m); this.name = 'LookupError'; } }
    class IndexError extends LookupError { constructor(m) { super(m); this.name = 'IndexError'; } }
    class KeyError extends LookupError { constructor(m) { super(m); this.name = 'KeyError'; } }
    class ValueError extends Exception { constructor(m) { super(m); this.name = 'ValueError'; } }
    class TypeError_ extends Exception { constructor(m) { super(m); this.name = 'TypeError'; } }
    class AttributeError extends Exception { constructor(m) { super(m); this.name = 'AttributeError'; } }
    class RuntimeError extends Exception { constructor(m) { super(m); this.name = 'RuntimeError'; } }
    class NotImplementedError extends RuntimeError { constructor(m) { super(m); this.name = 'NotImplementedError'; } }
    class RecursionError extends RuntimeError { constructor(m) { super(m); this.name = 'RecursionError'; } }
    class OSError extends Exception { constructor(m) { super(m); this.name = 'OSError'; } }
    class FileNotFoundError extends OSError { constructor(m) { super(m); this.name = 'FileNotFoundError'; } }
    class FileExistsError extends OSError { constructor(m) { super(m); this.name = 'FileExistsError'; } }
    class PermissionError extends OSError { constructor(m) { super(m); this.name = 'PermissionError'; } }
    class TimeoutError extends OSError { constructor(m) { super(m); this.name = 'TimeoutError'; } }
    class IsADirectoryError extends OSError { constructor(m) { super(m); this.name = 'IsADirectoryError'; } }
    class NotADirectoryError extends OSError { constructor(m) { super(m); this.name = 'NotADirectoryError'; } }
    class AssertionError extends Exception { constructor(m) { super(m); this.name = 'AssertionError'; } }
    class ImportError extends Exception { constructor(m) { super(m); this.name = 'ImportError'; } }
    class ModuleNotFoundError extends ImportError { constructor(m) { super(m); this.name = 'ModuleNotFoundError'; } }
    class NameError extends Exception { constructor(m) { super(m); this.name = 'NameError'; } }
    class UnboundLocalError extends NameError { constructor(m) { super(m); this.name = 'UnboundLocalError'; } }
    class StopIteration extends Exception { constructor(m) { super(m); this.name = 'StopIteration'; } }
    class StopAsyncIteration extends Exception { constructor(m) { super(m); this.name = 'StopAsyncIteration'; } }
    class EOFError extends Exception { constructor(m) { super(m); this.name = 'EOFError'; } }
    class MemoryError extends Exception { constructor(m) { super(m); this.name = 'MemoryError'; } }
    class ReferenceError_ extends Exception { constructor(m) { super(m); this.name = 'ReferenceError'; } }
    class KeyboardInterrupt extends BaseException { constructor(m) { super(m); this.name = 'KeyboardInterrupt'; } }
    class SystemExit extends BaseException { constructor(m) { super(m); this.name = 'SystemExit'; } }
    class GeneratorExit extends BaseException { constructor(m) { super(m); this.name = 'GeneratorExit'; } }
    return { BaseException, Exception, ArithmeticError, ZeroDivisionError, OverflowError, FloatingPointError, LookupError, IndexError, KeyError, ValueError, TypeError: TypeError_, AttributeError, RuntimeError, NotImplementedError, RecursionError, OSError, FileNotFoundError, FileExistsError, PermissionError, TimeoutError, IsADirectoryError, NotADirectoryError, AssertionError, ImportError, ModuleNotFoundError, NameError, UnboundLocalError, StopIteration, StopAsyncIteration, EOFError, MemoryError, ReferenceError: ReferenceError_, KeyboardInterrupt, SystemExit, GeneratorExit };
  })()
};
function pbStorageKey() {
  return "skooch_priority_board";
}
function pbNormalizeLabel(s) {
  let _raw = (s ? s : "");
  let _lower = String(_raw).toLowerCase();
  let _out = "";
  let _i = 0;
  while ((_i < _lower.length)) {
    let _ch = _jac.poly.getitem(_lower, _i);
    if ((((_ch >= "a") && (_ch <= "z")) || ((_ch >= "0") && (_ch <= "9")))) {
      _out = (_out + _ch);
    }
    _i = (_i + 1);
  }
  return _out;
}
function pbSeedChips() {
  let _seed = [{"label": "Sleep", "ic": "\ud83d\ude34", "custom": false, "zone": "1"}, {"label": "Exercise", "ic": "\ud83c\udfc3", "custom": false, "zone": "1"}, {"label": "Friends & family", "ic": "\ud83d\udc68\u200d\ud83d\udc69\u200d\ud83d\udc67", "custom": false, "zone": "2"}, {"label": "Work / School", "ic": "\ud83d\udcbc", "custom": false, "zone": "2"}, {"label": "Cooking", "ic": "\ud83c\udf73", "custom": false, "zone": "3"}, {"label": "Errands", "ic": "\ud83e\uddfe", "custom": false, "zone": "3"}, {"label": "Downtime", "ic": "\ud83d\udecb\ufe0f", "custom": false, "zone": "4"}, {"label": "Home & chores", "ic": "\ud83e\uddf9", "custom": false, "zone": "4"}];
  let _optional = [{"label": "Reading", "ic": "\ud83d\udcda", "custom": false, "zone": "optional"}, {"label": "Gaming", "ic": "\ud83c\udfae", "custom": false, "zone": "optional"}, {"label": "Dating", "ic": "\ud83d\udc9e", "custom": false, "zone": "optional"}, {"label": "Gardening", "ic": "\ud83e\udeb4", "custom": false, "zone": "optional"}, {"label": "Travel", "ic": "\u2708\ufe0f", "custom": false, "zone": "optional"}, {"label": "Pets", "ic": "\ud83d\udc3e", "custom": false, "zone": "optional"}, {"label": "Music", "ic": "\ud83c\udfb5", "custom": false, "zone": "optional"}, {"label": "Side projects", "ic": "\ud83d\udee0\ufe0f", "custom": false, "zone": "optional"}, {"label": "Volunteering", "ic": "\ud83e\udd1d", "custom": false, "zone": "optional"}, {"label": "Faith", "ic": "\ud83d\ude4f", "custom": false, "zone": "optional"}, {"label": "Creative", "ic": "\ud83c\udfa8", "custom": false, "zone": "optional"}, {"label": "Finances", "ic": "\ud83d\udcb8", "custom": false, "zone": "optional"}];
  let _all = [];
  let _i = 0;
  while ((_i < _seed.length)) {
    let _c = _seed.at(_i);
    _all = _all.concat([{"label": _c["label"], "ic": _c["ic"], "custom": false, "zone": _c["zone"], "category": pbChipCategory(_c["label"])}]);
    _i = (_i + 1);
  }
  let _j = 0;
  while ((_j < _optional.length)) {
    let _o = _optional.at(_j);
    _all = _all.concat([{"label": _o["label"], "ic": _o["ic"], "custom": false, "zone": "optional", "category": pbChipCategory(_o["label"])}]);
    _j = (_j + 1);
  }
  return _all;
}
function pbIsDuplicate(chips, label) {
  let _n = pbNormalizeLabel(label);
  if (!_n) {
    return false;
  }
  let _i = 0;
  while ((_i < chips.length)) {
    let _c = chips.at(_i);
    let _existing = "";
    try {
      _existing = String(_c["label"]);
    } catch (__jac_e) {
      if (true) {
        let _e1 = __jac_e;
        _existing = "";
      } else {
        throw __jac_e;
      }
    }
    if ((pbNormalizeLabel(_existing) === _n)) {
      return true;
    }
    _i = (_i + 1);
  }
  return false;
}
function pbMockChip(raw) {
  let _text = String((raw ? raw : "")).trim();
  if (!_text) {
    return null;
  }
  let _lower = _text.toLowerCase();
  let _map = [[["climb", "climbing", "bouldering"], "\ud83e\uddd7"], [["ski", "skiing", "snowboard"], "\u26f7\ufe0f"], [["bike", "biking", "cycling", "spin"], "\ud83d\udeb4"], [["hike", "hiking"], "\ud83e\udd7e"], [["tennis"], "\ud83c\udfbe"], [["basketball", "hoops"], "\ud83c\udfc0"], [["soccer", "football"], "\u26bd"], [["golf"], "\u26f3"], [["cricket"], "\ud83c\udfcf"], [["bowling", "bowl"], "\ud83c\udfb3"], [["baseball", "softball"], "\u26be"], [["volleyball"], "\ud83c\udfd0"], [["hockey"], "\ud83c\udfd2"], [["box", "boxing", "kickbox", "martial", "karate", "judo", "jiu"], "\ud83e\udd4a"], [["skate", "skating", "rollerblade"], "\ud83d\udefc"], [["surf", "surfing"], "\ud83c\udfc4"], [["gym", "lift", "lifting", "workout", "weights"], "\ud83c\udfcb\ufe0f"], [["coffee", "cafe", "espresso"], "\u2615"], [["church", "mass", "worship", "temple", "mosque"], "\u26ea"], [["pottery", "ceramics"], "\ud83c\udffa"], [["knit", "knitting", "crochet", "sew", "sewing"], "\ud83e\uddf6"], [["lab", "research", "experiment", "chemistry", "biology", "science"], "\u2697\ufe0f"], [["coding", "code", "program", "programming", "leetcode", "hack"], "\ud83d\udcbb"], [["study", "studying", "homework", "school", "exam"], "\ud83d\udcd6"], [["write", "writing", "journal", "journaling", "blog"], "\u270d\ufe0f"], [["language", "spanish", "french", "duolingo", "german"], "\ud83d\udde3\ufe0f"], [["photo", "photography"], "\ud83d\udcf8"], [["therapy", "counseling", "counsel"], "\ud83d\udcac"], [["doctor", "dentist", "appointment", "checkup"], "\ud83e\ude7a"], [["clean", "cleaning", "laundry", "chores", "tidy"], "\ud83e\uddfa"], [["grocer", "groceries", "shopping"], "\ud83d\uded2"], [["kids", "childcare", "parenting", "baby"], "\ud83d\udc76"], [["date night", "date"], "\ud83d\udc95"], [["nap", "rest"], "\ud83d\ude34"], [["podcast", "listen"], "\ud83c\udfa7"], [["movie", "movies", "netflix", "show", "cinema"], "\ud83c\udfac"], [["beach"], "\ud83c\udfd6\ufe0f"], [["nature", "outdoors", "park", "camping"], "\ud83c\udf33"], [["diy", "woodwork", "build"], "\ud83d\udd28"], [["dinner", "mom", "dad", "family", "sunday"], "\ud83c\udf5d"], [["run", "marathon", "jog"], "\ud83c\udfc3"], [["salsa", "dance", "dancing"], "\ud83d\udc83"], [["swim", "swimming", "pool", "open-water", "open water"], "\ud83c\udfca"], [["read", "book", "reading"], "\ud83d\udcda"], [["paint", "draw", "art", "creative", "craft"], "\ud83c\udfa8"], [["guitar", "piano", "music", "sing", "band"], "\ud83c\udfb5"], [["garden", "plant", "grow"], "\ud83e\udeb4"], [["pet", "dog", "cat", "walk the"], "\ud83d\udc3e"], [["travel", "trip", "flight", "vacation"], "\u2708\ufe0f"], [["meditat", "yoga", "mindful", "pray", "faith"], "\ud83e\uddd8"], [["save", "budget", "money", "finance"], "\ud83d\udcb8"], [["volunteer", "charity", "give back"], "\ud83e\udd1d"], [["game", "gaming", "play"], "\ud83c\udfae"], [["cook", "bake", "meal"], "\ud83c\udf73"], [["friend", "hangout", "social"], "\ud83e\uddd1\u200d\ud83e\udd1d\u200d\ud83e\uddd1"]];
  let _ic = "\u2728";
  let _mi = 0;
  while ((_mi < _map.length)) {
    let _kws = _map.at(_mi)[0];
    let _hit = false;
    let _ki = 0;
    while ((_ki < _kws.length)) {
      if ((_lower.indexOf(_jac.poly.getitem(_kws, _ki)) !== (-1))) {
        _hit = true;
      }
      _ki = (_ki + 1);
    }
    if (_hit) {
      _ic = _map.at(_mi)[1];
      break;
    }
    _mi = (_mi + 1);
  }
  let _stripLeads = ["i want to ", "i need to ", "i like to ", "i love to ", "i enjoy to ", "training for a ", "training for an ", "training for the ", "getting into ", "i want ", "i need ", "my "];
  let _cleaned = _lower;
  let _si = 0;
  while ((_si < _stripLeads.length)) {
    let _lead = _stripLeads.at(_si);
    if ((_cleaned.indexOf(_lead) === 0)) {
      _cleaned = _cleaned.slice(_lead.length);
      break;
    }
    _si = (_si + 1);
  }
  let _fillers = ["with my", "with the", "for a", "for an", "for the", "to the", "on the", "in the", "every", "each"];
  let _words = _jac.poly.call(_cleaned, "split", " ");
  let _kept = [];
  let _wi = 0;
  while ((_wi < _words.length)) {
    let _w = _jac.poly.getitem(_words, _wi);
    if ((_w && !_fillers.includes(_w))) {
      _kept = _kept.concat([_w]);
    }
    _wi = (_wi + 1);
  }
  let _cleanedStr = _jac.poly.call(_kept, "join", " ").trim();
  if (!_cleanedStr) {
    _cleanedStr = _text;
  }
  let _parts = _jac.poly.call(_cleanedStr, "split", " ");
  let _titled = [];
  let _pi = 0;
  while ((_pi < _parts.length)) {
    let _p = _jac.poly.getitem(_parts, _pi);
    if (_p) {
      _titled = _titled.concat([(_p[0].toUpperCase() + _p.slice(1).toLowerCase())]);
    }
    _pi = (_pi + 1);
  }
  let _label = _jac.poly.call(_titled, "join", " ");
  if ((_label.toLowerCase().indexOf("half marathon") !== (-1))) {
    _label = "Half-marathon";
  }
  if (!_label) {
    _label = _text;
  }
  return {"label": _label, "ic": _ic, "custom": true, "zone": "optional", "category": pbChipCategory(_text)};
}
function PriorityBoard(props) {
  let _wire = el => {
    if (!el) {
      return;
    }
    try {
      if (el.__skoochPbWired) {
        return;
      }
      el.__skoochPbWired = true;
      let _root = el;
      try {
        let _existing = document.getElementById("skooch-priority-board-css");
        if (!_existing) {
          let _style = document.createElement("style");
          _style.id = "skooch-priority-board-css";
          _style.textContent = pbStyleText();
          document.head.appendChild(_style);
        }
      } catch (__jac_e) {
        if (true) {
          let _ecss = __jac_e;
        } else {
          throw __jac_e;
        }
      }
      _root.__skoochPbSeq = 0;
      _root.__skoochPbChips = [];
      _root.__skoochPbSelectedId = "";
      let _mkId = () => {
        let _n = _root.__skoochPbSeq;
        _root.__skoochPbSeq = (_n + 1);
        return ("c" + String(_n));
      };
      let _saveState = () => {
        try {
          let _payload = {"chips": _root.__skoochPbChips, "v": 1};
          window.localStorage.setItem(pbStorageKey(), JSON.stringify(_payload));
        } catch (__jac_e) {
          if (true) {
            let _esv = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _loadState = () => {
        let _restored = [];
        try {
          let _rawStr = window.localStorage.getItem(pbStorageKey());
          if (_rawStr) {
            let _parsed = JSON.parse(_rawStr);
            if (((_parsed && _parsed["chips"]) && (_parsed["chips"].length > 0))) {
              _restored = _parsed["chips"];
            }
          }
        } catch (__jac_e) {
          if (true) {
            let _eld = __jac_e;
            _restored = [];
          } else {
            throw __jac_e;
          }
        }
        if ((_restored.length > 0)) {
          let _ri = 0;
          while ((_ri < _restored.length)) {
            let _rc = _restored.at(_ri);
            if (!_rc["id"]) {
              _rc["id"] = _mkId();
            }
            let _curIc = String((_jac.poly.contains(_rc, "ic") ? _rc["ic"] : ""));
            if (((_curIc === "\u2728") || (_curIc === ""))) {
              let _healed = pbMockChip((_jac.poly.contains(_rc, "label") ? _rc["label"] : ""));
              if (((_healed && _healed["ic"]) && (_healed["ic"] !== "\u2728"))) {
                _rc["ic"] = _healed["ic"];
              }
            }
            _ri = (_ri + 1);
          }
          return _restored;
        }
        let _seeded = pbSeedChips();
        _seeded.forEach(c => {
          c["id"] = _mkId();
        });
        return _seeded;
      };
      let _escapeHtml = s => {
        let _str = String((s ? s : ""));
        _str = _jac.poly.call(_str.split("&"), "join", "&amp;");
        _str = _jac.poly.call(_str.split("<"), "join", "&lt;");
        _str = _jac.poly.call(_str.split(">"), "join", "&gt;");
        _str = _jac.poly.call(_str.split("\""), "join", "&quot;");
        _str = _jac.poly.call(_str.split("'"), "join", "&#39;");
        return _str;
      };
      let _findChip = id => {
        let _i = 0;
        while ((_i < _root.__skoochPbChips.length)) {
          if ((_jac.poly.getitem(_root.__skoochPbChips, _i)["id"] === id)) {
            return _jac.poly.getitem(_root.__skoochPbChips, _i);
          }
          _i = (_i + 1);
        }
        return null;
      };
      let _optionalZone = _root.querySelector(".optional-zone");
      let _banner = _root.querySelector(".cp-banner");
      let _bannerText = _root.querySelector(".cp-text");
      let _noteOut = _root.querySelector(".note-out");
      let _noteInput = _root.querySelector(".note-input");
      let _tierZones = {"1": _root.querySelector(".tier-drop-1"), "2": _root.querySelector(".tier-drop-2"), "3": _root.querySelector(".tier-drop-3"), "4": _root.querySelector(".tier-drop-4")};
      let _placeChip = (id, zone) => {
        let _c = _findChip(id);
        if (!_c) {
          return;
        }
        _c["zone"] = zone;
        _root.__skoochPbSelectedId = "";
        _saveState();
        _render();
      };
      let _returnToOptional = id => {
        _placeChip(id, "optional");
      };
      let _selectChip = id => {
        if ((_root.__skoochPbSelectedId === id)) {
          _root.__skoochPbSelectedId = "";
        } else {
          _root.__skoochPbSelectedId = id;
        }
        _render();
      };
      let _removeChip = id => {
        _root.__skoochPbChips = _root.__skoochPbChips.filter(c => {
          return (c["id"] !== id);
        });
        if ((_root.__skoochPbSelectedId === id)) {
          _root.__skoochPbSelectedId = "";
        }
        _saveState();
        _render();
      };
      let _chipEl = c => {
        let _span = document.createElement("span");
        let _isPicked = (c["id"] === _root.__skoochPbSelectedId);
        let _cls = "chip";
        if (c["custom"]) {
          _cls = (_cls + " custom");
        }
        if (_isPicked) {
          _cls = (_cls + " picked");
        }
        _span.className = _cls;
        _span.setAttribute("draggable", "true");
        _span.dataset.id = c["id"];
        _span.innerHTML = ((("<span class=\"ic\">" + String(c["ic"])) + "</span>") + _escapeHtml(c["label"]));
        let _x = document.createElement("button");
        _x.className = "chip-x";
        _x.type = "button";
        _x.setAttribute("aria-label", ("Remove " + String(c["label"])));
        _x.textContent = "\u00d7";
        _x.addEventListener("click", ev => {
          ev.stopPropagation();
          _removeChip(c["id"]);
        });
        _span.appendChild(_x);
        _span.addEventListener("click", ev => {
          ev.stopPropagation();
          let _held = _root.__skoochPbSelectedId;
          if (((_held !== "") && (_held !== c["id"]))) {
            _placeChip(_held, c["zone"]);
          } else {
            _selectChip(c["id"]);
          }
        });
        _span.addEventListener("dragstart", ev => {
          ev.dataTransfer.setData("text/plain", c["id"]);
          ev.dataTransfer.effectAllowed = "move";
          _jac.poly.call(_span.classList, "add", "dragging");
        });
        _span.addEventListener("dragend", () => {
          _jac.poly.call(_span.classList, "remove", "dragging");
        });
        return _span;
      };
      let _focusNote = () => {
        try {
          if (_noteInput) {
            _noteInput.scrollIntoView({"behavior": "smooth", "block": "center"});
            setTimeout(() => {
              _noteInput.focus();
            }, 220);
          }
        } catch (__jac_e) {
          if (true) {
            let _efn = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _render = () => {
        try {
          if (_optionalZone) {
            _optionalZone.innerHTML = "";
          }
          let _tk = ["1", "2", "3", "4"];
          let _tki = 0;
          while ((_tki < _tk.length)) {
            let _z = _tierZones[_tk.at(_tki)];
            if (_z) {
              _z.innerHTML = "";
            }
            _tki = (_tki + 1);
          }
          let _optionalHas = false;
          let _ci = 0;
          while ((_ci < _root.__skoochPbChips.length)) {
            let _c = _jac.poly.getitem(_root.__skoochPbChips, _ci);
            if ((_c["zone"] === "optional")) {
              if (_optionalZone) {
                _optionalZone.appendChild(_chipEl(_c));
              }
              _optionalHas = true;
            } else {
              let _tz = _tierZones[_c["zone"]];
              if (_tz) {
                _tz.appendChild(_chipEl(_c));
              }
            }
            _ci = (_ci + 1);
          }
          if ((!_optionalHas && _optionalZone)) {
            let _empty = document.createElement("span");
            _empty.className = "optional-empty";
            _empty.textContent = "All added. Make your own \u2192";
            _optionalZone.appendChild(_empty);
          }
          if (_optionalZone) {
            let _tile = document.createElement("span");
            _tile.className = "add-own-tile";
            _tile.innerHTML = "<span class=\"ic\">\u2728</span> Add your own";
            _tile.addEventListener("click", () => {
              _focusNote();
            });
            _optionalZone.appendChild(_tile);
          }
          let _pk = ["1", "2", "3", "4"];
          let _pki = 0;
          while ((_pki < _pk.length)) {
            let _pz = _tierZones[_pk.at(_pki)];
            if ((_pz && (_pz.children.length === 0))) {
              let _dash = document.createElement("span");
              _dash.className = "tier-empty";
              _dash.textContent = "\u2014";
              _pz.appendChild(_dash);
            }
            _pki = (_pki + 1);
          }
          let _tiers = _root.querySelectorAll(".tier");
          let _hasSel = (_root.__skoochPbSelectedId !== "");
          let _ti = 0;
          while ((_ti < _tiers.length)) {
            if (_hasSel) {
              _jac.poly.call(_jac.poly.getitem(_tiers, _ti).classList, "add", "armed");
            } else {
              _jac.poly.call(_jac.poly.getitem(_tiers, _ti).classList, "remove", "armed");
            }
            _ti = (_ti + 1);
          }
          if (_banner) {
            if (_hasSel) {
              let _sc = _findChip(_root.__skoochPbSelectedId);
              if ((_sc && _bannerText)) {
                _bannerText.innerHTML = (((("<span class=\"pchip\">" + String(_sc["ic"])) + " ") + _escapeHtml(_sc["label"])) + "</span> is picked \u2014 tap a tier to place it");
              }
              _jac.poly.call(_banner.classList, "add", "show");
            } else {
              _jac.poly.call(_banner.classList, "remove", "show");
            }
          }
        } catch (__jac_e) {
          if (true) {
            let _erd = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _tiers = _root.querySelectorAll(".tier");
      let _twi = 0;
      while ((_twi < _tiers.length)) {
        let _tEl = _jac.poly.getitem(_tiers, _twi);
        let _zone = _tEl.getAttribute("data-tier");
        _tEl.addEventListener("click", () => {
          if ((_root.__skoochPbSelectedId !== "")) {
            _placeChip(_root.__skoochPbSelectedId, _zone);
          }
        });
        _tEl.addEventListener("dragover", ev => {
          ev.preventDefault();
          _jac.poly.call(_tEl.classList, "add", "dropok");
          ev.dataTransfer.dropEffect = "move";
        });
        _tEl.addEventListener("dragleave", ev => {
          if (!_tEl.contains(ev.relatedTarget)) {
            _jac.poly.call(_tEl.classList, "remove", "dropok");
          }
        });
        _tEl.addEventListener("drop", ev => {
          ev.preventDefault();
          _jac.poly.call(_tEl.classList, "remove", "dropok");
          let _id = ev.dataTransfer.getData("text/plain");
          if (_id) {
            _placeChip(_id, _zone);
          }
        });
        _twi = (_twi + 1);
      }
      if (_optionalZone) {
        _optionalZone.addEventListener("dragover", ev => {
          ev.preventDefault();
          _jac.poly.call(_optionalZone.classList, "add", "dropok");
        });
        _optionalZone.addEventListener("dragleave", ev => {
          if (!_optionalZone.contains(ev.relatedTarget)) {
            _jac.poly.call(_optionalZone.classList, "remove", "dropok");
          }
        });
        _optionalZone.addEventListener("drop", ev => {
          ev.preventDefault();
          _jac.poly.call(_optionalZone.classList, "remove", "dropok");
          let _id = ev.dataTransfer.getData("text/plain");
          if (_id) {
            _returnToOptional(_id);
          }
        });
        _optionalZone.addEventListener("click", () => {
          if ((_root.__skoochPbSelectedId !== "")) {
            let _c = _findChip(_root.__skoochPbSelectedId);
            if ((_c && (_c["zone"] !== "optional"))) {
              _returnToOptional(_root.__skoochPbSelectedId);
            }
          }
        });
      }
      let _cancelBtn = _root.querySelector(".cp-cancel");
      if (_cancelBtn) {
        _cancelBtn.addEventListener("click", () => {
          _root.__skoochPbSelectedId = "";
          _render();
        });
      }
      let _resetBtn = _root.querySelector(".pb-reset");
      if (_resetBtn) {
        _resetBtn.addEventListener("click", () => {
          let _seeded = pbSeedChips();
          _seeded.forEach(c => {
            c["id"] = _mkId();
          });
          _root.__skoochPbChips = _seeded;
          _root.__skoochPbSelectedId = "";
          if (_noteOut) {
            _jac.poly.call(_noteOut.classList, "remove", "show");
            _noteOut.innerHTML = "";
          }
          _saveState();
          _render();
        });
      }
      let _resolveChip = text => {
        return (async function __ability_exit_614_25__() {
          let _made = null;
          try {
            let _sb = window.__skoochSupabase;
            if ((_sb && _sb.functions)) {
              let _invokeP = _sb.functions.invoke("chip-from-note", {"body": {"text": text}});
              let _mkTimeout = (resolve, reject) => {
                setTimeout(() => {
                  resolve(null);
                }, 600);
              };
              let _timeoutP = Reflect.construct(Promise, [_mkTimeout]);
              let _resp = await Promise.race([_invokeP, _timeoutP]);
              let _data = ((_resp && _jac.poly.contains(_resp, "data")) ? _resp["data"] : null);
              if ((_data && _data["label"])) {
                let _suggested = (_jac.poly.contains(_data, "suggested_tier") ? _data["suggested_tier"] : null);
                let _suggCat = (_jac.poly.contains(_data, "suggested_category") ? _data["suggested_category"] : null);
                if (!_suggCat) {
                  _suggCat = pbChipCategory(text);
                }
                _made = {"label": String(_data["label"]), "ic": String((_data["emoji"] ? _data["emoji"] : "\u2728")), "custom": true, "zone": "optional", "category": pbChipCategory(_suggCat)};
                if (_suggested) {
                  _made["suggested_tier"] = _suggested;
                }
              }
            }
          } catch (__jac_e) {
            if (true) {
              let _eef = __jac_e;
              _made = null;
            } else {
              throw __jac_e;
            }
          }
          if (!_made) {
            _made = pbMockChip(text);
          }
          return _made;
        })();
      };
      let _addNote = () => {
        try {
          if (!_noteInput) {
            return;
          }
          let _val = _noteInput.value;
          if (!(_val && String(_val).trim())) {
            return;
          }
          let _p = _resolveChip(_val);
          _p.then(made => {
            try {
              if (!made) {
                return;
              }
              if (pbIsDuplicate(_root.__skoochPbChips, made["label"])) {
                let _dupId = "";
                let _dupIc = "";
                let _dupLabel = "";
                let _di = 0;
                while ((_di < _root.__skoochPbChips.length)) {
                  let _dc = _jac.poly.getitem(_root.__skoochPbChips, _di);
                  if ((pbNormalizeLabel(_dc["label"]) === pbNormalizeLabel(made["label"]))) {
                    _dupId = _dc["id"];
                    _dupIc = String(_dc["ic"]);
                    _dupLabel = String(_dc["label"]);
                  }
                  _di = (_di + 1);
                }
                _noteInput.value = "";
                if (_noteOut) {
                  _noteOut.innerHTML = (((("Already on your list \u2713 <span class=\"chip custom preview\"><span class=\"ic\">" + _dupIc) + "</span>") + _escapeHtml(_dupLabel)) + "</span>");
                  _jac.poly.call(_noteOut.classList, "add", "show");
                }
                let _ex = _root.querySelector((("[data-id=\"" + _dupId) + "\"]"));
                if (_ex) {
                  _ex.scrollIntoView({"behavior": "smooth", "block": "center"});
                  _jac.poly.call(_ex.classList, "add", "flash");
                  setTimeout(() => {
                    _jac.poly.call(_ex.classList, "remove", "flash");
                  }, 1000);
                }
                return;
              }
              let _newChip = {"id": _mkId(), "label": String(made["label"]), "ic": String((made["ic"] ? made["ic"] : "\u2728")), "custom": true, "zone": "optional", "category": pbChipCategory((_jac.poly.contains(made, "category") ? made["category"] : made["label"]))};
              _root.__skoochPbChips = _root.__skoochPbChips.concat([_newChip]);
              _noteInput.value = "";
              _saveState();
              _render();
              if (_noteOut) {
                _noteOut.innerHTML = (((("becomes \u2192 <span class=\"chip custom preview\"><span class=\"ic\">" + String(_newChip["ic"])) + "</span>") + _escapeHtml(_newChip["label"])) + "</span>");
                _jac.poly.call(_noteOut.classList, "add", "show");
              }
              let _node = (_optionalZone ? _optionalZone.querySelector((("[data-id=\"" + _newChip["id"]) + "\"]")) : null);
              if (_node) {
                _jac.poly.call(_node.classList, "add", "just-added");
              }
            } catch (__jac_e) {
              if (true) {
                let _ein = __jac_e;
              } else {
                throw __jac_e;
              }
            }
          });
        } catch (__jac_e) {
          if (true) {
            let _ean = __jac_e;
          } else {
            throw __jac_e;
          }
        }
      };
      let _noteAdd = _root.querySelector(".note-add");
      if (_noteAdd) {
        _noteAdd.addEventListener("click", () => {
          _addNote();
        });
      }
      if (_noteInput) {
        _noteInput.addEventListener("keydown", ev => {
          if ((ev.key === "Enter")) {
            _addNote();
          }
        });
      }
      let _exChips = _root.querySelectorAll(".ex-chip");
      let _exi = 0;
      while ((_exi < _exChips.length)) {
        let _exEl = _jac.poly.getitem(_exChips, _exi);
        _exEl.addEventListener("click", ev => {
          let _btn = ev.currentTarget;
          let _exVal = _btn.getAttribute("data-ex");
          if ((_noteInput && _exVal)) {
            _noteInput.value = _exVal;
            _addNote();
          }
        });
        _exi = (_exi + 1);
      }
      let _hadPrior = false;
      try {
        _hadPrior = (window.localStorage.getItem(pbStorageKey()) ? true : false);
      } catch (__jac_e) {
        if (true) {
          let _ehp = __jac_e;
          _hadPrior = false;
        } else {
          throw __jac_e;
        }
      }
      _root.__skoochPbChips = _loadState();
      if (!_hadPrior) {
        _saveState();
      }
      _render();
    } catch (__jac_e) {
      if (true) {
        let _eWire = __jac_e;
      } else {
        throw __jac_e;
      }
    }
  };
  return __jacJsx("div", {"className": "priority-board-root", "ref": _wire}, [__jacJsx("div", {"className": "pb-head"}, [__jacJsx("div", {"className": "pb-eyebrow"}, ["Your week, ranked"]), __jacJsx("h2", {"className": "pb-title"}, ["What deserves your best hours?"]), __jacJsx("p", {"className": "pb-sub"}, ["Your everyday basics are already ranked below \u2014 drag any to a different tier. Then add anything else that's part of your life, or write your own."])]), __jacJsx("div", {"className": "cp-banner"}, [__jacJsx("span", {"className": "dot"}, []), __jacJsx("span", {"className": "cp-text"}, ["picked \u2014 tap a tier to place it"]), __jacJsx("button", {"type": "button", "className": "cp-cancel"}, ["cancel"])]), __jacJsx("div", {"className": "board-card"}, [__jacJsx("div", {"className": "board-head"}, [__jacJsx("h3", {}, ["Your priorities"]), __jacJsx("span", {"className": "hint"}, ["Tap a chip, then tap a tier \u2014 or drag it."]), __jacJsx("button", {"type": "button", "className": "pb-reset"}, ["Reset"])]), __jacJsx("div", {"className": "tiers"}, [__jacJsx("div", {"className": "tier t1", "data-tier": "1"}, [__jacJsx("div", {"className": "tier-tab"}, [__jacJsx("span", {"className": "tier-letter"}, ["Tier 1"]), __jacJsx("span", {"className": "tier-name"}, ["Protect my best hours"])]), __jacJsx("div", {"className": "tier-drop tier-drop-1", "data-zone": "1"}, [])]), __jacJsx("div", {"className": "tier t2", "data-tier": "2"}, [__jacJsx("div", {"className": "tier-tab"}, [__jacJsx("span", {"className": "tier-letter"}, ["Tier 2"]), __jacJsx("span", {"className": "tier-name"}, ["Really matters"])]), __jacJsx("div", {"className": "tier-drop tier-drop-2", "data-zone": "2"}, [])]), __jacJsx("div", {"className": "tier t3", "data-tier": "3"}, [__jacJsx("div", {"className": "tier-tab"}, [__jacJsx("span", {"className": "tier-letter"}, ["Tier 3"]), __jacJsx("span", {"className": "tier-name"}, ["Fit it in"])]), __jacJsx("div", {"className": "tier-drop tier-drop-3", "data-zone": "3"}, [])]), __jacJsx("div", {"className": "tier t4", "data-tier": "4"}, [__jacJsx("div", {"className": "tier-tab"}, [__jacJsx("span", {"className": "tier-letter"}, ["Tier 4"]), __jacJsx("span", {"className": "tier-name"}, ["Low energy is fine"])]), __jacJsx("div", {"className": "tier-drop tier-drop-4", "data-zone": "4"}, [])])])]), __jacJsx("div", {"className": "cols"}, [__jacJsx("div", {"className": "optional-card"}, [__jacJsx("h3", {}, ["Everything else"]), __jacJsx("div", {"className": "sub"}, ["All optional \u2014 tap any that's part of your life to drop it into a tier, and skip the rest."]), __jacJsx("div", {"className": "optional-zone", "data-zone": "optional"}, [])]), __jacJsx("div", {"className": "note-card"}, [__jacJsx("h4", {}, [__jacJsx("span", {"className": "spark"}, ["\u2728"]), " ", "Make it yours"]), __jacJsx("div", {"className": "sub"}, ["Your life isn't a template. Tell Skooch anything else in your own words \u2014 it becomes a chip you can rank."]), __jacJsx("div", {"className": "note-examples"}, [__jacJsx("span", {"className": "lbl"}, ["Try:"]), __jacJsx("span", {"className": "ex-chip", "data-ex": "salsa class on tuesdays"}, ["\ud83d\udc83 Salsa nights"]), __jacJsx("span", {"className": "ex-chip", "data-ex": "sunday dinners with my mom"}, ["\ud83c\udf5d Sunday dinners"]), __jacJsx("span", {"className": "ex-chip", "data-ex": "training for a 10k"}, ["\ud83c\udfc3 Training for a 10k"])]), __jacJsx("div", {"className": "note-row"}, [__jacJsx("input", {"className": "note-input", "type": "text", "placeholder": "e.g. pottery class, dog walks, side hustle…", "aria-label": "Add your own priority"}, []), __jacJsx("button", {"type": "button", "className": "note-add"}, ["\u2728 Add"])]), __jacJsx("div", {"className": "note-out"}, []), __jacJsx("div", {"className": "note-tip"}, ["Lands in 'Everything else,' ready to rank."])])])]);
}
function pbStyleText() {
  return ((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((((".priority-board-root{--pb-bg-card:#fffefa;--pb-text-primary:#322f27;--pb-text-secondary:#716d5f;--pb-text-muted:#a8a18c;--pb-accent:#5F7A52;--pb-accent-hover:#4c6442;--pb-accent-light:rgba(95,122,82,0.12);--pb-accent-warm:#C98A1F;--pb-accent-warm-light:rgba(201,138,31,0.14);--pb-border:#e3ddcf;--pb-shadow-sm:0 1px 2px rgba(45,40,25,0.05);--pb-shadow-md:0 4px 14px rgba(45,40,25,0.08);--pb-radius-md:13px;--pb-radius-lg:18px;--pb-radius-xl:24px;color:var(--pb-text-primary)}" + ".priority-board-root *,.priority-board-root *::before,.priority-board-root *::after{box-sizing:border-box}") + ".pb-head{text-align:center;margin-bottom:18px}") + ".pb-eyebrow{font-size:.74rem;letter-spacing:.16em;text-transform:uppercase;color:var(--pb-accent);font-weight:700;margin-bottom:9px}") + ".pb-title{font-size:1.7rem;margin-bottom:7px;font-weight:500;letter-spacing:-0.01em}") + ".pb-sub{color:var(--pb-text-secondary);font-size:1rem;max-width:600px;margin:0 auto}") + ".priority-board-root .cp-banner{position:sticky;top:12px;z-index:50;display:none;align-items:center;justify-content:center;gap:10px;margin:0 auto 16px;max-width:560px;background:var(--pb-accent);color:#fff;border-radius:999px;padding:11px 18px;font-size:.9rem;font-weight:600;box-shadow:var(--pb-shadow-md)}") + ".priority-board-root .cp-banner.show{display:flex}") + ".priority-board-root .cp-banner .dot{width:9px;height:9px;border-radius:50%;background:#fff;animation:pbpulse 1.2s infinite;flex:0 0 9px}") + "@keyframes pbpulse{0%,100%{opacity:.45}50%{opacity:1}}") + ".priority-board-root .cp-banner .pchip{background:#fff;color:var(--pb-accent-hover);border-radius:999px;padding:3px 11px;font-weight:700;display:inline-flex;gap:5px;align-items:center}") + ".priority-board-root .cp-banner .cp-cancel{background:rgba(255,255,255,0.22);border:none;color:#fff;font-weight:600;font-size:.78rem;padding:5px 11px;border-radius:999px;cursor:pointer}") + ".priority-board-root .board-card{background:linear-gradient(150deg,#efe4cf,#e7d8bb);border:1px solid #d8c39a;border-radius:var(--pb-radius-xl);box-shadow:var(--pb-shadow-md),inset 0 0 60px rgba(120,90,50,0.07);padding:18px 20px 20px;margin-bottom:22px}") + ".priority-board-root .board-head{display:flex;align-items:baseline;justify-content:space-between;margin-bottom:13px;padding:0 2px}") + ".priority-board-root .board-head h3{font-size:1.12rem;color:#6f5326;font-weight:500}") + ".priority-board-root .board-head .hint{font-size:.8rem;color:#8a6f3e}") + ".priority-board-root .board-head .pb-reset{background:none;border:none;color:var(--pb-accent-hover);font-weight:600;font-size:.82rem;cursor:pointer;text-decoration:underline;text-underline-offset:2px}") + ".priority-board-root .tiers{display:flex;flex-direction:column;gap:11px}") + ".priority-board-root .tier{display:flex;align-items:stretch;background:var(--pb-bg-card);border:1px solid var(--pb-border);border-radius:var(--pb-radius-lg);box-shadow:var(--pb-shadow-sm);overflow:hidden;min-height:90px;transition:box-shadow .15s,border-color .15s}") + ".priority-board-root .tier-tab{flex:0 0 196px;display:flex;flex-direction:column;justify-content:center;gap:3px;padding:14px 18px;color:#fff}") + ".priority-board-root .t1 .tier-tab{background:var(--pb-accent)}") + ".priority-board-root .t2 .tier-tab{background:#7d9b6e}") + ".priority-board-root .t3 .tier-tab{background:#aeb98e;color:var(--pb-text-primary)}") + ".priority-board-root .t4 .tier-tab{background:#cdc8b1;color:var(--pb-text-primary)}") + ".priority-board-root .tier-letter{font-size:.74rem;font-weight:700;letter-spacing:.05em;text-transform:uppercase;opacity:.85}") + ".priority-board-root .tier-name{font-size:1.12rem;line-height:1.15}") + ".priority-board-root .tier-drop{flex:1;display:flex;align-items:flex-start;flex-wrap:wrap;gap:9px;padding:13px 16px;transition:background .15s}") + ".priority-board-root .tier-empty{font-size:.84rem;color:var(--pb-text-muted);font-style:italic}") + ".priority-board-root .tier.armed{border-color:var(--pb-accent);box-shadow:0 0 0 3px var(--pb-accent-light)}") + ".priority-board-root .tier.armed .tier-drop{background:var(--pb-accent-light);cursor:pointer}") + ".priority-board-root .tier.dropok{border-color:var(--pb-accent);box-shadow:0 0 0 3px var(--pb-accent-light)}") + ".priority-board-root .tier.dropok .tier-drop{background:var(--pb-accent-light)}") + ".priority-board-root .cols{display:flex;gap:18px;align-items:stretch}") + ".priority-board-root .optional-card{flex:1 1 60%;background:var(--pb-bg-card);border:1px solid var(--pb-border);border-radius:var(--pb-radius-lg);box-shadow:var(--pb-shadow-sm);padding:18px 20px 20px}") + ".priority-board-root .optional-card h3{font-size:1.1rem;margin-bottom:2px;font-weight:500}") + ".priority-board-root .optional-card .sub{font-size:.85rem;color:var(--pb-text-secondary);margin-bottom:14px}") + ".priority-board-root .optional-zone{display:flex;flex-wrap:wrap;gap:7px;min-height:64px;align-items:flex-start;padding:4px;border-radius:var(--pb-radius-md);transition:background .15s,box-shadow .15s}") + ".priority-board-root .optional-zone.dropok{background:var(--pb-accent-light);box-shadow:inset 0 0 0 2px var(--pb-accent)}") + ".priority-board-root .optional-empty{font-size:.84rem;color:var(--pb-text-muted);font-style:italic;padding:8px 4px}") + ".priority-board-root .note-card{flex:1 1 40%;background:linear-gradient(165deg,#fff6dc,#fbe6b0);border:1.5px solid var(--pb-accent-warm);border-radius:var(--pb-radius-lg);box-shadow:var(--pb-shadow-md),0 0 0 5px var(--pb-accent-warm-light);padding:22px 22px 20px;display:flex;flex-direction:column;position:relative}") + ".priority-board-root .note-card .spark{font-size:1.45rem;line-height:1;display:inline-block;animation:pbtwinkle 2.4s ease-in-out infinite}") + "@keyframes pbtwinkle{0%,100%{transform:scale(1) rotate(0);opacity:.85}50%{transform:scale(1.22) rotate(10deg);opacity:1}}") + ".priority-board-root .note-card h4{font-size:1.3rem;color:var(--pb-accent-warm);margin-bottom:4px;display:flex;align-items:center;gap:9px;font-weight:500}") + ".priority-board-root .note-card .sub{font-size:.9rem;color:#7a6238;margin-bottom:14px;line-height:1.45}") + ".priority-board-root .note-examples{display:flex;flex-wrap:wrap;gap:7px;margin-bottom:14px}") + ".priority-board-root .note-examples .lbl{font-size:.8rem;color:#9a7d44;align-self:center;margin-right:1px}") + ".priority-board-root .ex-chip{display:inline-flex;align-items:center;gap:5px;padding:6px 12px;background:rgba(255,255,255,0.75);border:1px dashed var(--pb-accent-warm);border-radius:999px;font-size:.82rem;font-weight:500;color:#6f5326;cursor:pointer;transition:background .15s,transform .12s}") + ".priority-board-root .ex-chip:hover{background:#fffefa;transform:translateY(-1px)}") + ".priority-board-root .note-row{display:flex;align-items:center;gap:8px;background:#fffefa;border:1.5px solid var(--pb-accent-warm);border-radius:999px;padding:7px 8px 7px 18px;box-shadow:var(--pb-shadow-sm)}") + ".priority-board-root .note-row .note-input{flex:1;border:none;background:transparent;font-size:1rem;color:var(--pb-text-primary);outline:none;min-width:0}") + ".priority-board-root .note-row .note-input::placeholder{color:var(--pb-text-muted)}") + ".priority-board-root .note-row .note-add{border:none;background:var(--pb-accent-warm);color:#fff;font-weight:600;font-size:.92rem;padding:11px 20px;border-radius:999px;cursor:pointer;white-space:nowrap}") + ".priority-board-root .note-row .note-add:hover{filter:brightness(1.06)}") + ".priority-board-root .add-own-tile{display:inline-flex;align-items:center;gap:7px;padding:9px 15px;background:var(--pb-accent-warm-light);border:1.5px dashed var(--pb-accent-warm);border-radius:999px;font-size:.9rem;font-weight:600;color:#6f5326;cursor:pointer;transition:background .15s,transform .12s}") + ".priority-board-root .add-own-tile:hover{background:rgba(201,138,31,0.22);transform:translateY(-2px)}") + ".priority-board-root .add-own-tile .ic{font-size:1.02rem}") + ".priority-board-root .note-out{margin-top:13px;font-size:.84rem;color:var(--pb-text-secondary);display:none;align-items:center;gap:8px;flex-wrap:wrap}") + ".priority-board-root .note-out.show{display:flex}") + ".priority-board-root .note-tip{margin-top:auto;padding-top:13px;font-size:.76rem;color:var(--pb-text-muted)}") + ".priority-board-root .chip{display:inline-flex;align-items:center;gap:5px;padding:6px 11px;background:var(--pb-bg-card);border:1px solid var(--pb-border);border-radius:999px;font-size:.83rem;font-weight:500;box-shadow:var(--pb-shadow-sm);cursor:pointer;user-select:none;transition:transform .12s,box-shadow .12s,background .12s}") + ".priority-board-root .chip .ic{font-size:.98rem;line-height:1}") + ".priority-board-root .chip:hover{transform:translateY(-2px);box-shadow:var(--pb-shadow-md)}") + ".priority-board-root .chip .ic{font-size:1.05rem;line-height:1}") + ".priority-board-root .chip.custom{background:linear-gradient(180deg,#fffdf6,var(--pb-accent-warm-light));border-color:var(--pb-accent-warm)}") + ".priority-board-root .chip.custom .ic{color:var(--pb-accent-warm)}") + ".priority-board-root .chip.picked{background:var(--pb-accent);color:#fff;border-color:var(--pb-accent);box-shadow:var(--pb-shadow-md);transform:translateY(-1px) scale(1.05)}") + ".priority-board-root .chip.picked.custom{background:var(--pb-accent-warm);border-color:var(--pb-accent-warm);color:#fff}") + ".priority-board-root .chip.preview{cursor:default;box-shadow:none}") + ".priority-board-root .chip.preview:hover{transform:none}") + ".priority-board-root .chip.dragging{opacity:.45}") + ".priority-board-root .chip.just-added{animation:pbpop .45s ease}") + "@keyframes pbpop{0%{transform:scale(.5);opacity:0}65%{transform:scale(1.09)}100%{transform:scale(1)}}") + ".priority-board-root .chip.flash{animation:pbflashring 1s ease}") + "@keyframes pbflashring{0%{box-shadow:0 0 0 0 var(--pb-accent)}35%{box-shadow:0 0 0 4px var(--pb-accent-light)}100%{box-shadow:var(--pb-shadow-sm)}}") + ".priority-board-root .chip-x{margin-left:1px;width:17px;height:17px;flex:0 0 17px;border:none;background:transparent;color:var(--pb-text-muted);font-size:1.05rem;line-height:1;cursor:pointer;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;padding:0;opacity:.6;transition:background .12s,color .12s,opacity .12s}") + ".priority-board-root .chip-x:hover{background:rgba(50,40,25,0.12);color:var(--pb-text-primary);opacity:1}") + ".priority-board-root .chip.picked .chip-x{color:rgba(255,255,255,0.85);opacity:.85}") + ".priority-board-root .chip.picked .chip-x:hover{background:rgba(255,255,255,0.28);color:#fff;opacity:1}") + "@media (max-width:760px){.priority-board-root .cols{flex-direction:column}}") + "@media (max-width:640px){.priority-board-root .tier{flex-direction:column}.priority-board-root .tier-tab{flex-basis:auto;flex-direction:row;align-items:baseline;gap:8px;padding:10px 14px}.priority-board-root .tier-name{font-size:1rem}.priority-board-root .tier-drop{padding:10px 12px}.priority-board-root .board-card{padding:13px 12px 15px}.priority-board-root .optional-card{padding:13px 12px 15px}.priority-board-root .note-card{padding:15px 13px}}") + ".onboarding-card:has(.priority-board-root){max-width:1120px;padding-left:20px;padding-right:20px}") + "@media (max-width:680px){.onboarding-card:has(.priority-board-root){padding-left:12px;padding-right:12px}}") + ".onboarding-card:has(.priority-board-root) .onboarding-content{padding-left:2px;padding-right:2px}") + "@media (min-width:900px){.priority-board-root{display:grid;grid-template-columns:minmax(0,1.2fr) minmax(0,0.8fr);column-gap:18px;align-items:stretch}.priority-board-root .pb-head{grid-column:1/-1;grid-row:1}.priority-board-root .cp-banner{grid-column:1/-1;grid-row:2}.priority-board-root .board-card{grid-column:1;grid-row:3;margin-bottom:0;display:flex;flex-direction:column}.priority-board-root .cols{grid-column:2;grid-row:3;flex-direction:column;margin-top:0}.priority-board-root .cols .optional-card,.priority-board-root .cols .note-card{flex:0 0 auto}.priority-board-root .pb-head{margin-bottom:6px}.priority-board-root .pb-title{font-size:1.5rem;margin-bottom:2px}.priority-board-root .pb-sub{display:none}.priority-board-root .board-card{padding:13px 18px 15px}.priority-board-root .board-head{margin-bottom:9px}.priority-board-root .tiers{gap:9px;flex:1}.priority-board-root .tier{flex:1;min-height:86px}.priority-board-root .tier-tab{padding:11px 18px}.priority-board-root .tier-drop{padding:12px 16px}.priority-board-root .optional-card,.priority-board-root .note-card{padding:14px 16px}}") + "@media (min-width:900px){.onboarding-card:has(.priority-board-root){padding-top:14px;padding-bottom:14px}.onboarding-card:has(.priority-board-root) .onboarding-brand{margin-bottom:4px}.onboarding-card:has(.priority-board-root) .onboarding-subtitle{margin-bottom:8px}.onboarding-card:has(.priority-board-root) .onboarding-progress{margin-bottom:6px}.onboarding-card:has(.priority-board-root) .onboarding-step-label{margin-bottom:8px}}");
}
export {PriorityBoard, pbIsDuplicate, pbMockChip, pbNormalizeLabel, pbSeedChips, pbStorageKey, pbStyleText};
if (typeof globalThis !== "undefined") { if (!globalThis.__jacEndpointEffects__) globalThis.__jacEndpointEffects__ = {}; Object.assign(globalThis.__jacEndpointEffects__, {"func:pbStorageKey": {"is_writer": false, "touches": ["*"]}, "func:pbNormalizeLabel": {"is_writer": false, "touches": ["*"]}, "func:pbSeedChips": {"is_writer": false, "touches": ["*"]}, "func:pbIsDuplicate": {"is_writer": false, "touches": ["*"]}, "func:pbMockChip": {"is_writer": false, "touches": ["*"]}, "func:PriorityBoard": {"is_writer": true, "touches": ["*"]}, "func:pbStyleText": {"is_writer": false, "touches": ["*"]}}); };
//# sourceMappingURL=priority_board.js.map
